import type { Tab } from './index';
import { esc, filterTable } from '../utils';
import { fetchSheet } from '../worker';
import { fetchAlpacaSnapshot, type PriceData } from '../alpaca';
import { writeToSheet } from '../sheets';
import { showToast } from '../toast';
import { store } from '../state';

interface TickerRow {
  _id:               string | number;
  Ticker:            string;
  Company:           string;
  Exchange?:         string;
  Sector?:           string;
  Date_Researched?:  string;
  Price_At_Research?:string;
  Market_Cap?:       string;
  Revenue_TTM?:      string;
  Profitable?:       string;
  Key_Thesis?:       string;
  Risk_Factors?:     string;
  Notes?:            string;
}

interface FieldDef {
  key:         string;
  label:       string;
  type?:       'text' | 'select' | 'textarea';
  placeholder?: string;
  options?:    string[];
}

const FIELDS: FieldDef[] = [
  { key:'Ticker',            label:'Ticker Symbol',  placeholder:'e.g. PLTR' },
  { key:'Company',           label:'Company Name',   placeholder:'e.g. Palantir Technologies' },
  { key:'Exchange',          label:'Exchange',        type:'select', options:['NASDAQ','NYSE','OTC','TSX','OTHER'] },
  { key:'Sector',            label:'Sector / Tags',  placeholder:'e.g. AI / Defense Software' },
  { key:'Date_Researched',   label:'Date Researched',placeholder:'e.g. 2026-05-23' },
  { key:'Price_At_Research', label:'Entry Price',    placeholder:'e.g. $24.50' },
  { key:'Market_Cap',        label:'Market Cap',     placeholder:'e.g. $52B' },
  { key:'Revenue_TTM',       label:'Revenue TTM',    placeholder:'e.g. $2.9B' },
  { key:'Profitable',        label:'Profitable?',    type:'select', options:['Yes','No'] },
  { key:'Key_Thesis',        label:'Key Thesis',     type:'textarea', placeholder:'Why is this interesting?' },
  { key:'Risk_Factors',      label:'Risk Factors',   type:'textarea', placeholder:'What could go wrong?' },
  { key:'Notes',             label:'Notes',          type:'textarea', placeholder:'Any additional notes...' },
];

export class HoldingsTab implements Tab {
  readonly id = 'tracker';

  private rendered  = false;
  private priceCache = new Map<string, { data: PriceData; ts: number }>();
  private pendingSave: ((data: Record<string, string>) => Promise<void>) | null = null;

  private el(id: string): HTMLElement {
    return document.getElementById(id) as HTMLElement;
  }

  init(): void {
    this.el('tracker-sync-btn').addEventListener('click',       () => this.sync());
    this.el('tracker-live-btn').addEventListener('click',       () => this.refreshPrices());
    this.el('tracker-add-btn').addEventListener('click',        () => this.openAdd());
    this.el('tracker-filter').addEventListener('input', e =>
      filterTable('tracker-table-dynamic', (e.target as HTMLInputElement).value),
    );

    // Edit/delete via event delegation
    this.el('tracker-table-dynamic').addEventListener('click', e => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
      if (!btn) return;
      const row = (store.get('tickers') as TickerRow[]).find(r => String(r._id) === btn.dataset.id);
      if (!row) return;
      if (btn.dataset.action === 'edit')   this.openEdit(row);
      if (btn.dataset.action === 'delete') this.delete(row);
      // Chart/options/trade stubs — wire when those tabs are migrated
      if (btn.dataset.action === 'chart')   showToast('Chart tab not yet migrated', 'warn');
      if (btn.dataset.action === 'options') showToast('Options tab not yet migrated', 'warn');
      if (btn.dataset.action === 'buy')     showToast('Paper trade not yet migrated', 'warn');
      if (btn.dataset.action === 'sell')    showToast('Paper trade not yet migrated', 'warn');
    });
  }

  render(): void {
    if (this.rendered) return;  // already showing — don't flash table on every tab visit
    const cached = store.get('tickers') as TickerRow[];
    if (cached.length) {
      this.renderRows(cached);
      this.rendered = true;
    } else {
      this.sync();
    }
  }

  private async sync(): Promise<void> {
    const statusEl = this.el('db-ticker-status');
    statusEl.textContent = 'Loading from Google Sheets…';

    try {
      const data = await fetchSheet<TickerRow>('Tickers');
      if (data.status !== 'ok') throw new Error(data.message ?? 'Sheet load failed');
      const rows = data.rows ?? [];
      store.set('tickers', rows);
      this.renderRows(rows);

      if (rows.length === 0) {
        statusEl.textContent = 'No data in Tickers sheet yet — add your first ticker below!';
        return;
      }

      this.el('db-dynamic-section').style.display = 'block';
      this.el('db-setup-banner').style.display     = 'none';
      this.el('db-tracker-count').textContent      = String(rows.length);
      this.el('db-status-badge').textContent       = `Connected ✓ — ${rows.length} rows`;
      this.el('db-status-badge').style.color       = 'var(--green)';
      statusEl.textContent = `✓ ${rows.length} tickers loaded · last sync ${new Date().toLocaleTimeString()}`;

      showToast(`✓ Loaded ${rows.length} tickers from Sheets`);
      this.rendered = true;
      setTimeout(() => this.refreshPrices(), 500);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      statusEl.textContent = 'Load failed: ' + msg;
      showToast('Tickers load failed: ' + msg, 'error');
    }
  }

  private renderRows(rows: TickerRow[]): void {
    const tbody = this.el('tracker-table-dynamic').querySelector('tbody')!;
    tbody.innerHTML = rows.map((r, i) => this.buildRow(r, i)).join('');
    if (rows.length > 0) {
      this.el('db-dynamic-section').style.display = 'block';
      this.el('db-setup-banner').style.display     = 'none';
    }
  }

  private buildRow(row: TickerRow, idx: number): string {
    const profitable  = (row.Profitable ?? '').toLowerCase() === 'yes';
    const entryRaw    = parseFloat(String(row.Price_At_Research ?? '').replace(/[$,]/g, ''));
    const entryPrice  = isNaN(entryRaw) ? '' : String(entryRaw);
    const id          = String(row._id);
    const sectors     = (row.Sector ?? '').split('/').map(s => `<span class="tag">${esc(s.trim())}</span>`).join('');

    return `<tr data-idx="${idx}" data-ticker="${esc(row.Ticker)}">
      <td>
        <span class="ticker-badge">${esc(row.Ticker)}</span>
        <div style="font-size:10px;color:var(--text3);margin-top:2px;">${esc(row.Exchange ?? '')}</div>
      </td>
      <td><strong>${esc(row.Company)}</strong></td>
      <td>${sectors}</td>
      <td class="text-muted">${esc(row.Date_Researched ?? '—')}</td>
      <td><strong>${esc(row.Price_At_Research ?? '—')}</strong></td>
      <td id="live-price-${esc(row.Ticker)}" style="font-weight:700;color:var(--text);">
        <span style="color:var(--text3);font-size:11px;">⟳</span>
      </td>
      <td id="live-change-${esc(row.Ticker)}" style="font-size:12px;">—</td>
      <td id="live-return-${esc(row.Ticker)}" style="font-size:12px;" data-entry="${esc(entryPrice)}">—</td>
      <td class="text-muted">${esc(row.Market_Cap ?? '—')}</td>
      <td class="text-muted">${esc(row.Revenue_TTM ?? '—')}</td>
      <td>${profitable ? '<span class="yes">✓ YES</span>' : '<span class="no">✗ NO</span>'}</td>
      <td class="thesis-cell">${esc(row.Key_Thesis ?? '—')}</td>
      <td class="risk-cell">${esc(row.Risk_Factors ?? '—')}</td>
      <td class="notes-cell">${esc(row.Notes ?? '—')}</td>
      <td>
        ${this.chartLinks(row.Ticker)}
        <div style="display:flex;gap:4px;margin-top:6px;">
          <button data-action="chart"   data-id="${id}" style="background:rgba(0,212,255,0.08);border:1px solid rgba(0,212,255,0.2);color:var(--accent);font-family:var(--mono);font-size:10px;padding:2px 7px;cursor:pointer;border-radius:2px;" title="Chart">📈</button>
          <button data-action="options" data-id="${id}" style="background:rgba(255,184,48,0.08);border:1px solid rgba(255,184,48,0.2);color:var(--warn);font-family:var(--mono);font-size:10px;padding:2px 7px;cursor:pointer;border-radius:2px;" title="Options">⛓️</button>
          <button data-action="buy"     data-id="${id}" style="background:rgba(46,213,115,0.08);border:1px solid rgba(46,213,115,0.2);color:var(--green);font-family:var(--mono);font-size:10px;padding:2px 7px;cursor:pointer;border-radius:2px;" title="Paper Buy">▲</button>
          <button data-action="sell"    data-id="${id}" style="background:rgba(255,71,87,0.08);border:1px solid rgba(255,71,87,0.2);color:var(--red);font-family:var(--mono);font-size:10px;padding:2px 7px;cursor:pointer;border-radius:2px;" title="Paper Sell">▼</button>
          <button data-action="edit"    data-id="${id}" style="background:rgba(0,212,255,0.08);border:1px solid rgba(0,212,255,0.2);color:var(--accent);font-family:var(--mono);font-size:10px;padding:2px 7px;cursor:pointer;border-radius:2px;">✏️</button>
          <button data-action="delete"  data-id="${id}" style="background:rgba(255,71,87,0.08);border:1px solid rgba(255,71,87,0.2);color:var(--red);font-family:var(--mono);font-size:10px;padding:2px 7px;cursor:pointer;border-radius:2px;">🗑️</button>
        </div>
      </td>
    </tr>`;
  }

  private chartLinks(ticker: string): string {
    const t = ticker.toUpperCase().trim();
    return `<div class="quick-links">
      <a class="chart-link" href="https://finance.yahoo.com/quote/${t}/" target="_blank" rel="noopener">↗ Yahoo</a>
      <a class="chart-link" href="https://stockanalysis.com/stocks/${t.toLowerCase()}/" target="_blank" rel="noopener">↗ Analysis</a>
      <a class="chart-link" href="https://finviz.com/quote.ashx?t=${t}" target="_blank" rel="noopener">↗ Finviz</a>
      <a class="chart-link" href="https://www.macrotrends.net/stocks/charts/${t}/" target="_blank" rel="noopener">↗ Macro</a>
    </div>`;
  }

  // ── Live prices ───────────────────────────────────────────────────────────

  private async refreshPrices(): Promise<void> {
    const btn = this.el('tracker-live-btn') as HTMLButtonElement;
    btn.textContent = '⟳ FETCHING…';
    btn.style.opacity = '0.6';

    const rows = this.el('tracker-table-dynamic')
      .querySelectorAll<HTMLElement>('tbody tr[data-ticker]');
    const tickers = [...new Set(Array.from(rows).map(r => r.dataset.ticker ?? '').filter(Boolean))];

    if (!tickers.length) {
      showToast('No tickers loaded — sync first', 'warn');
      btn.textContent = '💹 LIVE PRICES';
      btn.style.opacity = '1';
      return;
    }

    const BATCH = 5;
    let fetched = 0;
    for (let i = 0; i < tickers.length; i += BATCH) {
      await Promise.all(tickers.slice(i, i + BATCH).map(async ticker => {
        const cached = this.priceCache.get(ticker);
        if (cached && Date.now() - cached.ts < 30000) {
          this.updatePriceCells(ticker, cached.data);
          fetched++;
          return;
        }
        const data = await fetchAlpacaSnapshot(ticker);
        if (data) {
          this.priceCache.set(ticker, { data, ts: Date.now() });
          this.updatePriceCells(ticker, data);
          fetched++;
        } else {
          this.updatePriceCells(ticker, null);
        }
      }));
      if (i + BATCH < tickers.length) await new Promise(r => setTimeout(r, 400));
    }

    this.el('db-ticker-status').textContent =
      `✓ Prices updated · ${new Date().toLocaleTimeString()} · Alpaca IEX`;
    btn.textContent = '💹 LIVE PRICES';
    btn.style.opacity = '1';
    showToast(`💹 Prices updated for ${fetched} tickers`);
  }

  private updatePriceCells(ticker: string, data: PriceData | null): void {
    const priceEl  = document.getElementById(`live-price-${ticker}`);
    const changeEl = document.getElementById(`live-change-${ticker}`);
    const returnEl = document.getElementById(`live-return-${ticker}`);

    if (!data) {
      if (priceEl) priceEl.innerHTML = '<span style="color:var(--text3);font-size:10px;">N/A</span>';
      return;
    }

    const fmt = (n: number) => `$${n.toFixed(2)}`;
    const col  = data.change >= 0 ? 'var(--green)' : 'var(--red)';
    const arr  = data.change >= 0 ? '▲' : '▼';

    if (priceEl) {
      priceEl.innerHTML = `<strong style="color:var(--text);font-size:13px;">${fmt(data.price)}</strong>`;
      priceEl.style.transition  = 'background 0.3s';
      priceEl.style.background  = data.change >= 0 ? 'rgba(46,213,115,0.1)' : 'rgba(255,71,87,0.1)';
      setTimeout(() => { priceEl.style.background = 'transparent'; }, 800);
    }
    if (changeEl) {
      changeEl.innerHTML = `<span style="color:${col};font-size:11px;">${arr} ${fmt(data.change)}<br>${data.changePct >= 0 ? '+' : ''}${data.changePct.toFixed(2)}%</span>`;
    }
    if (returnEl) {
      const entry = parseFloat((returnEl as HTMLElement).dataset.entry ?? '');
      if (entry > 0) {
        const ret = (data.price - entry) / entry * 100;
        const rc  = ret >= 0 ? 'var(--green)' : 'var(--red)';
        returnEl.innerHTML = `<span style="color:${rc};font-size:11px;font-weight:700;">${ret >= 0 ? '▲ +' : '▼ '}${ret.toFixed(2)}%</span>`;
      } else {
        returnEl.innerHTML = '<span style="color:var(--text3);font-size:10px;">No entry</span>';
      }
    }
  }

  // ── CRUD ───────────────────────────────────────────────────────────────────

  private openAdd(): void {
    this.showModal('➕ Add New Ticker', {}, async data => {
      const ok = await writeToSheet('append', 'Tickers', data);
      if (ok) { showToast(`✓ ${data['Ticker']} added to tracker!`); setTimeout(() => this.sync(), 1500); }
    });
  }

  private openEdit(row: TickerRow): void {
    this.showModal(`✏️ Edit — ${row.Ticker}`, row as unknown as Record<string, string>, async data => {
      const ok = await writeToSheet('update', 'Tickers', { ...data, _id: row._id });
      if (ok) { showToast(`✓ ${data['Ticker']} updated!`); setTimeout(() => this.sync(), 1500); }
    });
  }

  private async delete(row: TickerRow): Promise<void> {
    if (!confirm(`Delete ${row.Ticker} — ${row.Company}?\nThis will remove it from your Google Sheet permanently.`)) return;
    const ok = await writeToSheet('delete', 'Tickers', { _id: row._id });
    if (ok) { showToast(`🗑️ ${row.Ticker} removed.`, 'warn'); setTimeout(() => this.sync(), 1500); }
  }

  // ── Modal ─────────────────────────────────────────────────────────────────

  private showModal(
    title: string,
    prefill: Record<string, string>,
    onSave: (data: Record<string, string>) => Promise<void>,
  ): void {
    document.getElementById('holdings-modal')?.remove();
    this.pendingSave = onSave;

    const base = 'width:100%;background:var(--panel);border:1px solid var(--border);color:var(--text);font-family:var(--mono);font-size:12px;padding:8px 10px;border-radius:3px;outline:none;box-sizing:border-box;';
    const fields = FIELDS.map(f => {
      const full = f.type === 'textarea' ? 'grid-column:1/-1;' : '';
      let inp: string;
      if (f.type === 'select') {
        const opts = (f.options ?? []).map(o =>
          `<option value="${esc(o)}"${prefill[f.key] === o ? ' selected' : ''}>${esc(o)}</option>`
        ).join('');
        inp = `<select id="dbf_${f.key}" style="${base}">${opts}</select>`;
      } else if (f.type === 'textarea') {
        inp = `<textarea id="dbf_${f.key}" rows="3" style="${base}resize:vertical;" placeholder="${esc(f.placeholder ?? '')}">${esc(prefill[f.key] ?? '')}</textarea>`;
      } else {
        inp = `<input id="dbf_${f.key}" type="text" value="${esc(prefill[f.key] ?? '')}" placeholder="${esc(f.placeholder ?? '')}" style="${base}">`;
      }
      return `<div style="${full}">
        <label style="display:block;font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:var(--text3);margin-bottom:4px;">${esc(f.label)}</label>
        ${inp}
      </div>`;
    }).join('');

    const modal = document.createElement('div');
    modal.id = 'holdings-modal';
    modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.8);z-index:10000;display:flex;align-items:center;justify-content:center;padding:20px;overflow-y:auto;';
    modal.innerHTML = `
      <div style="background:var(--bg2);border:1px solid var(--border);border-radius:6px;padding:28px;width:100%;max-width:600px;max-height:90vh;overflow-y:auto;">
        <div style="font-family:var(--sans);font-size:18px;font-weight:800;color:var(--text);margin-bottom:20px;">${esc(title)}</div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">${fields}</div>
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:20px;">
          <button id="holdings-modal-cancel" style="background:none;border:1px solid var(--border);color:var(--text2);font-family:var(--mono);font-size:11px;padding:8px 16px;cursor:pointer;border-radius:3px;">CANCEL</button>
          <button id="holdings-modal-save"   style="background:rgba(46,213,115,0.1);border:1px solid rgba(46,213,115,0.3);color:var(--green);font-family:var(--mono);font-size:11px;padding:8px 20px;cursor:pointer;border-radius:3px;">SAVE</button>
        </div>
      </div>`;

    document.body.appendChild(modal);
    modal.addEventListener('click', e => { if (e.target === modal) modal.remove(); });
    modal.querySelector('#holdings-modal-cancel')!.addEventListener('click', () => modal.remove());
    modal.querySelector('#holdings-modal-save')!.addEventListener('click',   () => this.submitModal());
    setTimeout(() => (document.getElementById('dbf_Ticker') as HTMLInputElement | null)?.focus(), 100);
  }

  private async submitModal(): Promise<void> {
    if (!this.pendingSave) return;
    const data: Record<string, string> = {};
    FIELDS.forEach(f => {
      const el = document.getElementById(`dbf_${f.key}`) as HTMLInputElement | null;
      if (el) data[f.key] = el.value.trim();
    });
    if (!data['Ticker']) { showToast('Ticker symbol is required', 'warn'); return; }
    document.getElementById('holdings-modal')?.remove();
    await this.pendingSave(data);
    this.pendingSave = null;
  }
}
